import { useState, useEffect } from 'react';

import { Row, Col, Card, Table, Space, Typography, Button, Collapse, DatePicker, message, Modal, InputNumber, Tag, Alert, Progress } from 'antd';
import { SUBSCRIPTION_STATUS, PAYMENT_PRODUCT_TYPE } from '../../../../constants/AppConstants';
import Data from '../../../CustomText/data';
import {
  convertCentsIntoDollar,
  formatDate,
  isSupportManager,
  isSupportLead,
  isSupportAgent2,
  isSupportAgent3,
  findFurnishingAccount
} from '../../../../helpers/genericHelper';
import API from '../../../../services/API';
import StatementView from './StatementView';
import TransactionHistoryComponent from '../TransactionHistory';
import PaymentLinkCheckoutModal from '../Payments/PaymentLinkCheckoutModal';
import VoidTransactionsModal from './VoidTransactionsModal'
import PaymentLinkPanel from '../Payments/PaymentLinksPanel';
import CreditLimitIncentivesComponent from "./CreditLimitIncentivesComponent";
import SpendLimitChangesComponent from "./SpendLimitChangesComponent";
import CliEligibilitySummary from "./CliEligibilitySummary";
import StatementsList from './StatementsList';
import DeclinedTransactionsComponent from '../DeclinedTransactions';
import MonthlySpendPowerComponent from '../MonthlySpendPower';
import PaymentHistoryComponent from '../PaymentHistory';
import PaymentAttemptsComponent from '../PaymentAttempts';
import ActiveSubscriptions from '../ActiveSubscriptions';
import { Document, PDFDownloadLink } from '@react-pdf/renderer';
import moment from 'moment';
import { CCStatement } from './Statement';
import AutoPayAction from '../Actions/AutoPayAction';
import useMerchantActivation from './useMerchantActivation';
import MerchantListModal from './MerchantListModal';
import { getAutoPayStatus } from '../../../../helpers/parsingHelper';
import { PersistedCollapse } from '../../../PersistedSections';
import ActionsMenu from '../../../ActionsMenu';

const { Text } = Typography;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;

const CountTag = ({ count, label, problem = false }) => {
  if (count == null) return null;
  return <Tag color={problem && count > 0 ? "error" : "default"}>{label ? `${count} ${label}` : count}</Tag>;
};

const BigValue = ({ children, type }) => <Text strong type={type} style={{ fontSize: 22, display: 'block' }}>{children}</Text>;

export default function AccountView({
  creditCard, setShowSpendPowerModal, userId, domain, userSummary,
  openChangeDueDateModal,
  showChargeSilaModal, showCancelPaymentModal, refreshList, paymentAttempts, autoPayStatus,
  furnishingAccounts
}) {

  const account = creditCard;
  const card = creditCard;

  const [statements, setStatements] = useState([]);
  const [loadingStatements, setLoadingStatements] = useState(false);
  const [activity, setActivity] = useState([]);

  const [startDate, setStartDate] = useState(moment().subtract(6, 'months'));
  const [endDate, setEndDate] = useState(moment());

  const [merchantCycleLoading, setMerchantCycleLoading] = useState(false);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundAmount, setRefundAmount] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);
  const [merchantListModalVisible, setMerchantListModalVisible] = useState(false);

  const merchantActivation = useMerchantActivation({ userId });
  const autoPayStatusData = getAutoPayStatus(autoPayStatus, PAYMENT_PRODUCT_TYPE.CREDIT_CARD);

  const loadStatementAndActivities = async () => {
    setLoadingStatements(true);
    try {
      setStatements(await API.getCreditCardStatements({ accountId: card?.accountId }));
      setActivity(await API.getCreditCardsActivity({ accountId: card?.accountId }));
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingStatements(false);
    }
  }

  useEffect(() => {
    loadStatementAndActivities();
  }, [card.id]);

  const getSpendModeTooltip = (spendModeSummary) => {
    const spendMode = spendModeSummary?.spendMode;
    const txnLimit = convertCentsIntoDollar(spendModeSummary?.transactionCentsLimitPerMerchantPerDay || 0);
    const merchantLimit = spendModeSummary?.merchantLimitPerMonth || 0;

    if (spendMode === "SPEND_LIMIT") {
      return "Spend Limit mode allows the customer to use their card in the classic, spend power manner per billing cycle.";
    } else if (spendMode === "TRANSACTION_LIMIT") {
      return `Transaction Limit mode allows the customer to make up to ${merchantLimit} charges per day of at most ${txnLimit} with at most ${merchantLimit} merchants per month.`;
    } else {
      return "Unknown Spend Mode. Please contact Eng.";
    }
  };

  const createNewCard = async () => {
    if (window.confirm("Are you sure you want to create a new card? Older card(s) will be cancelled.")) {
      await API.createNewCreditCard({ userId });
      refreshList();
    }
  }

  const resetMerchantCycle = async () => {
    try {
      setMerchantCycleLoading(true)
      await API.resetMerchantCycle(userId, account.accountId);
      message.success(`Successfully reset the merchant cycle`);
    } catch (e) {
      message.error('Unable to reset the merchant cycle');
      console.error('Unable reset the merchant cycle', e)
    } finally {setMerchantCycleLoading(false);}
  };

  const deleteMerchantActivationMerchantByAva = async (merchantName) => {
    try {
      await API.deleteMerchantActivationMerchantByAva({userId, merchantName});
      message.success(`Successfully undeleted the merchant`);
      merchantActivation.getMerchantActivation();
    } catch (e) {
      message.error('Unable to undelete the merchant');
      console.error('Unable undelete the merchant', e)
    }
  };

  const handleRefundNegativeBalance = async () => {
    if (!refundAmount || refundAmount <= 0) {
      message.error('Please enter a valid refund amount');
      return;
    }
    const refundAmountCents = Math.round(refundAmount * 100);
    try {
      setRefundLoading(true);
      await API.refundNegativeBalance({
        userId,
        creditCardAccountId: account.accountId,
        refundAmountCents
      });
      message.success('Negative balance refund initiated successfully');
      setRefundModalVisible(false);
      setRefundAmount(null);
      refreshList();
    } catch (ex) {
      message.error(
        ex?.response?.data?.error_message || ex?.response?.data?.errorMessage || 'Failed to process refund'
      );
    } finally {
      setRefundLoading(false);
    }
  };

  const pillColor = (value) => {
    switch (value) {
      case 'ACTIVE':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'DELINQUENT':
        return 'error';
      case 'CHARGED_OFF':
      case 'CANCELLED':
      case 'CLOSED':
        return 'error';
      default:
        return 'default';
    }
  }

  const isTransactionLimitMode = (spendModeSummary) => {
    return (spendModeSummary?.spendMode === "TRANSACTION_LIMIT");
  };

  // Calculate minimum bank balance requirements
  const paymentAmount = statements?.toSorted(
    (a,b) => new Date(b.statementData.statementDate) - new Date(a.statementData.statementDate)
  )?.[0]?.statementData?.statementBalance
    || account?.accountDetails?.paymentDueInCents || 0;
  const autopayMinBalance = Math.max(10000, Math.round(paymentAmount * 1.5));
  const manualPaymentMinBalance = Math.max(5000, Math.round(paymentAmount * 1.5));

  // Current outstanding balance uses the actual net/settled balance (not the amount-due field,
  // which the backend floors to $0 for credit balances). A negative net balance is a credit
  // the user is owed, so surface it explicitly as "$X.XX credit" rather than a bare "-$X.XX".
  const currentBalanceCents = domain?.domain?.balances?.currentBalance?.netBalance?.centsValue;
  const currentBalanceDisplay = currentBalanceCents < 0
    ? `${convertCentsIntoDollar(Math.abs(currentBalanceCents))} credit`
    : convertCentsIntoDollar(currentBalanceCents);

  const lastStatement = statements?.[0] || {};
  const lastFour = domain?.card?.[0]?.lastFour;
  const data = (activity?.activities || [])
    .filter((activity) => activity.success)
    .map((activity) => {
      return {
        debit: activity.item.creditCardStatementDataEntityType === "PAYMENT" || activity.item.amount < 0,
        transactionTime: activity.time,
        descriptor: activity.item.descriptor,
        amountInCents: activity.item.amount > 0 ? activity.item.amount : activity.item.amount * -1,
        balance: activity.item.balance
      }
    })

  const merchants = [...(merchantActivation?.merchantsList || []), ...(merchantActivation?.deletedMerchants || [])];
  const activeMerchants = merchants.filter((merchant) => !merchant.isDeleted && !merchant.deletedByAva).length;

  const accountStatus = domain?.account?.accountStatus;
  const currentCard = domain?.card?.find((c) => c.cardStatus === 'ACTIVE') ?? domain?.card?.[0];
  const isCardCancelled = account.cardStatus === SUBSCRIPTION_STATUS.CANCELLED;
  const isAutoPayOn = !(autoPayStatus?.isCreditCardAutoPayDisabled == true);
  const amountDueCents = domain?.domain?.balances?.currentAmountDue;
  const creditLimitCents = account?.creditCardMetaData?.creditLimitInCents;
  const usedPercent = creditLimitCents > 0 && currentBalanceCents > 0
    ? Math.min(100, Math.round(currentBalanceCents / creditLimitCents * 100))
    : 0;
  const totalSpendPowerCents = account?.spendPowerSummary?.totalSpendPowerCents;
  const spendPowerPercent = totalSpendPowerCents > 0
    ? Math.min(100, Math.round((account?.spendPowerUsed || 0) / totalSpendPowerCents * 100))
    : 0;
  const isDelinquent = account?.accountDetails?.isDelinquent || !!domain?.account?.accountDelinquentTs;
  const dueDay = domain?.account?.userRequestedPaymentDueDay ?? domain?.account?.paymentDueDay;
  // The tradeline this card is reported to the bureaus under
  const tradeline = findFurnishingAccount(
    furnishingAccounts,
    [creditCard?.id, creditCard?.accountId, domain?.account?.id, domain?.account?.accountId, currentCard?.id]
  );

  const alerts = [
    domain?.account?.accountChargeoffTs && {
      type: 'error',
      message: `Account charged off on ${formatDate(domain.account.accountChargeoffTs)}.`
    },
    domain?.account?.accountClosureTs && {
      type: 'error',
      message: `Account closed on ${formatDate(domain.account.accountClosureTs)}.`
    },
    isDelinquent && {
      type: 'error',
      message: `Delinquent${domain?.account?.accountDelinquentTs ? ` since ${formatDate(domain.account.accountDelinquentTs)}` : ''}.${amountDueCents > 0
        ? ` ${convertCentsIntoDollar(amountDueCents)} due${account?.accountDetails?.dueDate ? ` (was due ${formatDate(account.accountDetails.dueDate)})` : ''}.`
        : ''}`
    },
    !isAutoPayOn && { type: 'warning', message: 'AutoPay is off for this card.' },
    currentBalanceCents < 0 && {
      type: 'info',
      message: `The user has a ${convertCentsIntoDollar(Math.abs(currentBalanceCents))} credit balance.`
    }
  ].filter(Boolean);

  const actionGroups = [
    {
      title: 'Payments',
      items: [
        isSupportAgent2() && <Button key="retry" onClick={() => showChargeSilaModal(account)}>Retry Payment</Button>,
        (isSupportLead() || isSupportManager()) &&
          <Button key="refund" onClick={() => setRefundModalVisible(true)}>Refund Negative Balance</Button>,
        isSupportAgent2() && <AutoPayAction
          key="autopay"
          autoPayStatusEnabled={isAutoPayOn}
          refreshList={refreshList}
          type={PAYMENT_PRODUCT_TYPE.CREDIT_CARD}
          userId={userId}
          originalAutoPayDay={autoPayStatusData?.autoPayDay}
        />,
        isSupportManager() && <PaymentLinkCheckoutModal key="payment-link" refreshList={refreshList}
          userId={userId}
          ccAccountId={account.accountId}
          ccData={account}/>
      ]
    },
    {
      title: 'Card',
      items: [
        isSupportAgent2() && !isCardCancelled &&
          <Button key="due-day" onClick={() => openChangeDueDateModal(account)}>Change Due Day</Button>,
        isSupportLead() && <Button key="spend-power" onClick={() => setShowSpendPowerModal(true)}>Add Spend Power</Button>,
        isSupportAgent3() &&
          <Button key="merchant-cycle" onClick={resetMerchantCycle} loading={merchantCycleLoading}>Reset Merchant Cycle</Button>
      ]
    },
    {
      title: 'Danger Zone',
      danger: true,
      items: [
        isSupportManager() && <VoidTransactionsModal key="void" refreshList={refreshList}
          creditCardTransactions={account.creditCardTransactions}
        />,
        isSupportAgent2() && !isCardCancelled &&
          <Button key="new-card" danger onClick={() => createNewCard()}>Create New Card</Button>,
        isSupportAgent2() && !isCardCancelled &&
          <Button key="close" danger onClick={() => showCancelPaymentModal(account)}>Close Account</Button>
      ]
    }
  ];

  const downloadStatement = (
    <Space wrap size={8}>
      <RangePicker
        size="small"
        value={[startDate, endDate]}
        onChange={(dates) => {
          setStartDate(dates[0]);
          setEndDate(dates[1]);
        }}
      />
      <PDFDownloadLink
        document={
          <Document>
            <CCStatement
              title="Ava Credit Builder Card Statement"
              summaryData={userSummary}
              startDate={startDate}
              endDate={endDate}
              last4={lastFour}
              balanceDue={"$0.00"}
              list={data || []} />
          </Document>
        }
        fileName={`Ava Statement, ${moment(startDate).format('MMM-DD-YYYY')} - ${moment(endDate).format('MMM-DD-YYYY')}`}
      >
        {({ documentLoading }) => <Button size="small">{documentLoading ? 'Downloading Statement...' : 'Download Statement'}</Button>}
      </PDFDownloadLink>
    </Space>
  );

  return (
    <div className="summary-page card-view">
      {/* Header */}
      <Card size="small" className="summary-header">
        <div className="summary-header-row">
          <div className="summary-card-art">
            <span>ava</span>
            <span className="card-art-number">•••• {currentCard?.lastFour || '----'}</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Space size={8} wrap>
              <Text strong style={{ fontSize: 18 }}>Credit Builder Card</Text>
              <Tag color={pillColor(accountStatus)}>{accountStatus || 'N/A'}</Tag>
              {currentCard?.cardStatus && <Tag color={pillColor(currentCard.cardStatus)}>Card {currentCard.cardStatus}</Tag>}
            </Space>
            <div className="card-header-info">
              <Space size={6} wrap split={<Text type="secondary">·</Text>}>
                <Tag color="success">Provider: {domain?.account?.creditCardProvider || 'N/A'}</Tag>
                <Text>Opened {formatDate(domain?.account?.createTs) || 'N/A'}</Text>
                <Text>
                  Tradeline Number:{' '}
                  {tradeline?.accountNumber
                    ? <Text strong copyable>{tradeline.accountNumber}</Text>
                    : <Text type="secondary">—</Text>}
                </Text>
              </Space>
            </div>
            <div className="card-header-info">
              <Space size={6} wrap split={<Text type="secondary">·</Text>}>
                <Text>Billing day {domain?.account?.billingStatementDay ?? 'N/A'}</Text>
                <Text>
                  Due day {dueDay ?? 'N/A'}
                  {domain?.account?.userRequestedPaymentDueDay != null &&
                    <> (was {domain?.account?.paymentDueDay}, set by user{domain?.account?.dueDaySetTs ? ` ${formatDate(domain.account.dueDaySetTs)}` : ''})</>}
                </Text>
                <Tag color={isAutoPayOn ? 'success' : 'error'}>
                  AutoPay {isAutoPayOn ? 'On' : 'Off'}{isAutoPayOn && autoPayStatusData?.autoPayDay ? ` · Day ${autoPayStatusData.autoPayDay}` : ''}
                </Tag>
              </Space>
            </div>
          </div>
          <div className="card-header-balance">
            <Text type="secondary"><small>Current Outstanding Balance</small></Text>
            <div className="card-header-balance-value">{currentBalanceDisplay ?? '—'}</div>
          </div>
          <ActionsMenu groups={actionGroups} />
        </div>
      </Card>

      {alerts.length > 0 &&
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          {alerts.map((alert) => <Alert key={alert.message} showIcon type={alert.type} message={alert.message} />)}
        </Space>
      }

      {/* Balance and spend power */}
      <Row gutter={[12, 12]}>
        <Col xs={24} xl={13}>
          <Card size="small" title="Balance" className="summary-detail-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Data title="Current Outstanding Balance"><BigValue>{currentBalanceDisplay}</BigValue></Data>
              <div style={{ textAlign: 'right' }}>
                <Data title="Credit Limit"><BigValue>{convertCentsIntoDollar(creditLimitCents)}</BigValue></Data>
              </div>
            </div>
            <Progress percent={usedPercent} showInfo={false} strokeColor="#a448ff" />
            <Text type="secondary"><small>{usedPercent}% of credit limit used</small></Text>
            <div className="summary-detail-grid" style={{ marginTop: 12 }}>
              <Data title="Current Amount Due">
                {/* Delinquent: the amount in red with a red Delinquent tag, and the missed due date, as in the old design */}
                <Space size={6} wrap>
                  <Text strong style={amountDueCents > 0 && isDelinquent ? { color: '#cf1322', fontSize: 16 } : undefined}>
                    {convertCentsIntoDollar(amountDueCents)}
                  </Text>
                  {isDelinquent && <Tag color="error" style={{ fontWeight: 600, margin: 0 }}>Delinquent</Tag>}
                  {(account?.accountDetails?.paymentDueInCents > 0 || isDelinquent) && account?.accountDetails?.dueDate &&
                    <Tag color="warning" style={{ margin: 0 }}>Due {formatDate(account.accountDetails.dueDate)}</Tag>}
                </Space>
              </Data>
              <Data title="Amount Spent" value={convertCentsIntoDollar(account?.accountDetails?.amountSpentInCents)} />
              <Data title="Max Balance">
                {convertCentsIntoDollar(account?.maxAccountBalance?.balanceInCents)}
                {account?.maxAccountBalance?.balanceInCents > 0 && <Text type="secondary"> on {formatDate(account?.maxAccountBalance?.balanceDate)}</Text>}
              </Data>
              <Data title="Credit Limit Increase">
                <CliEligibilitySummary creditCard={domain} />
              </Data>
              <Data title="Min Balance for Autopay">
                {convertCentsIntoDollar(autopayMinBalance)} <Text type="secondary"><small>(max of 150% balance due or $100)</small></Text>
              </Data>
              <Data title="Min Balance for Manual Payment">
                {convertCentsIntoDollar(manualPaymentMinBalance)} <Text type="secondary"><small>(max of 150% balance due or $50)</small></Text>
              </Data>
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={11}>
          <Card size="small" title="Spend Power" className="summary-detail-card">
            <Data title="Spend Power (Used)">
              <BigValue>
                {convertCentsIntoDollar(account?.spendPowerUsed)}
                <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}> of {convertCentsIntoDollar(totalSpendPowerCents)}</Text>
              </BigValue>
            </Data>
            <Progress percent={spendPowerPercent} showInfo={false} strokeColor="#a448ff" />
            <Text type="secondary"><small>{convertCentsIntoDollar(account?.spendPowerAvailable)} available</small></Text>
            <div className="summary-detail-grid" style={{ marginTop: 12 }}>
              <Data title="Spend Mode">
                {account?.spendModeSummary?.spendMode || 'N/A'}
                {account?.spendModeSummary?.modeUpdateDate &&
                  <Text type="secondary"> (updated {formatDate(account.spendModeSummary.modeUpdateDate)})</Text>}
              </Data>
              <Data title="Merchant Activation">
                {merchantActivation.isEnrolled ? 'Enrolled' : 'Not Enrolled'}
                <Text type="secondary"> · {activeMerchants} active</Text>
              </Data>
              {isTransactionLimitMode(account?.spendModeSummary) &&
                <Data title="Transaction Limit" value={convertCentsIntoDollar(account?.spendModeSummary?.transactionCentsLimitPerMerchantPerDay)} />}
              {isTransactionLimitMode(account?.spendModeSummary) &&
                <Data title="Max Merchants Per Day (+ per Month)" value={account?.spendModeSummary?.merchantLimitPerMonth} />}
              <div style={{ gridColumn: '1 / -1' }}>
                <Text type="secondary"><small>{getSpendModeTooltip(account?.spendModeSummary)}</small></Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Grouped sections */}
      <PersistedCollapse name={`card-groups:${account?.id}`} className="summary-groups">
        <Panel
          header="Statements and Payments"
          key="statements"
          extra={<Space size={4}>
            <CountTag count={loadingStatements ? null : statements?.length ?? 0} label="statements" />
            <CountTag count={account.creditCardPayments?.length ?? 0} label="payments" />
          </Space>}>
          <PersistedCollapse name={`card-statement-sections:${account?.id}`} ghost defaultActiveKey={['last-statement']}>
            <Panel header="Last Statement" key="last-statement" extra={<div onClick={(e) => e.stopPropagation()}>{downloadStatement}</div>}>
              {lastStatement?.statementId
                ? <StatementView statement={lastStatement} />
                : <Row className="no-data-card">No Data Found</Row>}
            </Panel>
            <Panel header={<>Statements <CountTag count={loadingStatements ? null : statements?.length ?? 0} /></>} key="statements">
              <StatementsList statements={statements} />
            </Panel>
            <Panel header={<>Payment History <CountTag count={account.creditCardPayments?.length ?? 0} /></>} key="payments">
              <PaymentHistoryComponent payments={account.creditCardPayments} />
            </Panel>
            <Panel header="Failed Payment Attempts" key="failed">
              <PaymentAttemptsComponent accountId={card?.accountId} paymentAttempts={paymentAttempts} />
            </Panel>
          </PersistedCollapse>
        </Panel>

        <Panel
          header="Transactions"
          key="transactions"
          extra={<Space size={4}>
            <CountTag count={account.creditCardTransactions?.length ?? 0} label="transactions" />
            <CountTag count={account.declinedTransactions?.length ?? 0} label="declined" problem />
          </Space>}>
          <PersistedCollapse name={`card-transaction-sections:${account?.id}`} ghost>
            <Panel header={<>Transaction History <CountTag count={account.creditCardTransactions?.length ?? 0} /></>} key="history">
              <TransactionHistoryComponent
                transactions={account.creditCardTransactions}
                activities={activity?.activities || []}
              />
            </Panel>
            <Panel header={<>Declined Transactions <CountTag count={account.declinedTransactions?.length ?? 0} problem /></>} key="declined">
              <DeclinedTransactionsComponent declinedTransactions={account.declinedTransactions} />
            </Panel>
          </PersistedCollapse>
        </Panel>

        <Panel
          header="Credit Limit and Spend Power"
          key="limits"
          extra={<CountTag count={account.creditLimitChanges?.length ?? 0} label="limit changes" />}>
          <PersistedCollapse name={`card-limit-sections:${account?.id}`} ghost>
            <Panel header="Credit Limit Details (CLI)" key="cli">
              <CreditLimitIncentivesComponent
                  accountId={account?.id}
                  domain={domain}
                  creditLimitChanges={account.creditLimitChanges}
                  baseCreditLimit={account.baseCreditLimit}
              />
            </Panel>
            <Panel header="Spend Power Breakdown" key="spend-power">
              {account?.spendLimitChanges && account.spendLimitChanges.length > 0 ? (
                  <SpendLimitChangesComponent spendLimitChanges={account.spendLimitChanges} domain={domain}/>
              ) : (
                  <MonthlySpendPowerComponent
                      accountId={account?.id}
                      data={account?.spendPowerSummary}
                      boostsPending={account?.boostsPending}
                      spendLimitChanges={account?.spendLimitChanges}
                  />
              )}
            </Panel>
          </PersistedCollapse>
        </Panel>

        <Panel
          header="Cards, Merchants and Subscriptions"
          key="merchants"
          extra={<Space size={4}>
            <CountTag count={domain?.card?.length ?? 0} label="cards" />
            <CountTag count={activeMerchants} label="merchants" />
            <CountTag count={account?.activeSubscriptions?.length ?? 0} label="subscriptions" />
          </Space>}>
          <PersistedCollapse name={`card-merchant-sections:${account?.id}`} ghost>
            <Panel header={<>Cards <CountTag count={domain?.card?.length ?? 0} /></>} key="cards">
              <Table
                size="small"
                bordered
                pagination={false}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                dataSource={domain?.card || []}
                locale={{ emptyText: 'No cards found' }}
                columns={[
                  { title: 'Status', dataIndex: 'cardStatus', key: 'cardStatus', render: (status) => <Tag color={pillColor(status)}>{status}</Tag> },
                  { title: 'Last Four', dataIndex: 'lastFour', key: 'lastFour' },
                  { title: 'Created', dataIndex: 'createTs', key: 'createTs', render: (ts) => (ts ? formatDate(ts) : '—') },
                  { title: 'Exp', key: 'exp', render: (_, card) => (card.expMonth && card.expYear ? `${card.expMonth} / ${card.expYear}` : '—') },
                  { title: 'Closed', dataIndex: 'cardClosureTs', key: 'cardClosureTs', render: (ts) => (ts ? formatDate(ts) : '—') },
                  { title: 'Closure Reason', dataIndex: 'cardClosureReason', key: 'cardClosureReason', render: (reason) => (reason ? <small>{reason}</small> : '—') },
                  { title: 'Provider', dataIndex: 'creditCardProvider', key: 'creditCardProvider', render: (provider) => provider || '—' },
                ]}
              />
            </Panel>
            <Panel header={<>Merchant Activation <Tag>{merchantActivation.isEnrolled ? 'Enrolled' : 'Not Enrolled'}</Tag></>} key="merchants">
              {/* Flex-wrap, not grid breakpoints: wraps when the table's own width no longer fits beside the
                  spend limit. A fixed half (or a viewport breakpoint) let the table overflow into it. */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px' }}>
                <div style={{ flex: '1 0 auto' }}>
                  <Table
                    size="small"
                    bordered
                    pagination={false}
                    rowKey="merchantGroupName"
                    dataSource={merchants}
                    locale={{ emptyText: 'No merchants found' }}
                    // No per-merchant amount: spend limits are account-level since AVADEV-4175.
                    columns={[
                      { title: 'Merchant', dataIndex: 'displayName', key: 'displayName' },
                      {
                        title: 'Status',
                        key: 'status',
                        render: (_, merchant) => {
                          if (merchant.deletedByAva) return <Typography.Text type="secondary">Deleted by Ava</Typography.Text>;
                          if (merchant.isDeleted) return <Typography.Text type="secondary">Deleted</Typography.Text>;
                          return 'Active';
                        },
                      },
                      {
                        title: '',
                        key: 'action',
                        render: (_, merchant) => {
                          if (!isSupportAgent2()) return null;
                          if (merchant.deletedByAva) {
                            return <Button type="link" size="small" onClick={() => deleteMerchantActivationMerchantByAva(merchant.merchantGroupName)}>Undelete</Button>;
                          }
                          if (!merchant.isDeleted) {
                            return <Button type="link" size="small" danger onClick={() => merchantActivation.deleteMerchant(merchant)}>Delete</Button>;
                          }
                          return null;
                        },
                      },
                    ]}
                  />
                  { isSupportAgent2() && <Button
                    type="link"
                    style={{ paddingLeft: 0 }}
                    onClick={() => setMerchantListModalVisible(true)}
                  >
                    View All ({merchantActivation.merchantToAddList?.length || 0})
                  </Button> }
                </div>
                <div style={{ flex: '1 0 200px' }}>
                  <Data title="Merchant Spend Limit (shared by all merchants)" >
                    {convertCentsIntoDollar(merchantActivation.totalSpendLimit)}
                  </Data>
                </div>
              </div>
            </Panel>
            <Panel header={<>Active Subscriptions <CountTag count={account?.activeSubscriptions?.length ?? 0} /></>} key="subscriptions">
              <ActiveSubscriptions activeSubscriptions={account?.activeSubscriptions} />
            </Panel>
            <Panel header="Payment Links" key="payment-links">
              <PaymentLinkPanel userId={userId} />
            </Panel>
          </PersistedCollapse>
        </Panel>
      </PersistedCollapse>

      <MerchantListModal
        open={merchantListModalVisible}
        onClose={() => setMerchantListModalVisible(false)}
        merchants={merchantActivation.merchantToAddList}
        totalSpendLimit={merchantActivation.totalSpendLimit}
        minSpendLimit={merchantActivation.minSpendLimit}
        userId={userId}
        onAdded={merchantActivation.getMerchantActivation}
      />

      <Modal
        title="Refund Negative Balance"
        open={refundModalVisible}
        onOk={handleRefundNegativeBalance}
        onCancel={() => { setRefundModalVisible(false); setRefundAmount(null); }}
        confirmLoading={refundLoading}
        okText="Refund"
      >
        <p>Enter the refund amount in dollars:</p>
        <InputNumber
          min={1}
          precision={2}
          prefix="$"
          style={{ width: '100%' }}
          value={refundAmount}
          onChange={setRefundAmount}
          placeholder="e.g. 25.00"
        />
      </Modal>
    </div>
  )
}
